// source: template-project@f47fa8d21 apps/backoffice/src/pages/onboarding-new/presence.ts (dropped upstream by 7007a338a; no onboarding-v2 equivalent, so the older pin stays)
/**
 * Social and marketplace links in the website field. Someone who pastes their
 * Instagram or Amazon store gave us a real signal (where the brand lives), not
 * a typo: we keep it and then ask for the store's website, instead of running
 * the research on instagram.com or amazon.com as if they were the brand.
 */

export type Presence = {
  /** Stable id: 'instagram', 'amazon', … */
  platform: string
  /** Display name: "Instagram", "Amazon", … */
  label: string
  /** The account, shop or store name when the link carries one. */
  handle?: string
  /** True for stores (Amazon, Etsy, Walmart): selling there is itself a fact about the brand. */
  marketplace: boolean
}

type Rule = { platform: string; label: string; host: RegExp; marketplace?: boolean; handle?: (path: string[]) => string | undefined }

const first = (path: string[]) => path[0]?.replace(/^@/, '') || undefined
const after = (key: string) => (path: string[]) => {
  const i = path.findIndex((p) => p.toLowerCase() === key)
  return i >= 0 ? path[i + 1] || undefined : undefined
}

const RULES: Rule[] = [
  { platform: 'instagram', label: 'Instagram', host: /^instagram\.com$/, handle: first },
  { platform: 'tiktok', label: 'TikTok', host: /^tiktok\.com$/, handle: first },
  { platform: 'facebook', label: 'Facebook', host: /^(facebook|fb)\.com$/, handle: first },
  { platform: 'youtube', label: 'YouTube', host: /^youtube\.com$/, handle: first },
  { platform: 'pinterest', label: 'Pinterest', host: /^pinterest\.[a-z.]+$/, handle: first },
  { platform: 'linktree', label: 'Linktree', host: /^linktr\.ee$/, handle: first },
  { platform: 'amazon', label: 'Amazon', host: /^amazon\.[a-z.]+$/, marketplace: true, handle: after('stores') },
  { platform: 'etsy', label: 'Etsy', host: /^etsy\.com$/, marketplace: true, handle: after('shop') },
  { platform: 'walmart', label: 'Walmart', host: /^walmart\.com$/, marketplace: true },
]

/** The social or marketplace presence a pasted link points to, or null for an ordinary website. */
export function detectPresence(input: string): Presence | null {
  const bare = input.trim().replace(/^https?:\/\//i, '').replace(/^(www|m)\./i, '')
  const [host = '', ...rest] = bare.split(/[/?#]/)
  const path = rest.filter(Boolean)
  const rule = RULES.find((r) => r.host.test(host.toLowerCase()))
  if (!rule) return null
  return { platform: rule.platform, label: rule.label, handle: rule.handle?.(path), marketplace: !!rule.marketplace }
}
